import React, { useEffect, useState } from 'react';
import SubmitButton from '../../../components/SubmitButton';
import Label from '../../../components/Label';
import { axiosKK } from '../../../services/networkRequest';
import {
  ApplyMode,
  BillingRateDTO,
  PartnerAdditionalFeeDTO,
  PartnerPricingRequest,
  PartnerPricingResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import {
  AddButton,
  AdminNotice,
  DataTable,
  DataTableColumn,
} from '../../../components/admin/Shared';
import { DEFAULT_RATE_AMOUNTS_PENCE } from './defaults';

type Scope = 'courier' | 'warehouse';

const APPLY_MODES: ApplyMode[] = ['FLAT', 'PER_BOX', 'PER_DELIVERY', 'WEEKLY'];

// Canonical rate-type keys — must match RateType.kt on the backend
// (storage-api/src/uk/co/kitkeeper/booking/models/RateType.kt). Sending an
// unknown key returns 400 "rateType X is not valid for scope Y".
const KNOWN_COURIER_RATE_TYPES: string[] = [
  'DOORSTEP_COLLECTION',
  'TIMESLOT_COLLECTION',
  'DOORSTEP_RETURN',
  'TIMESLOT_RETURN',
  'MIN_CHARGE',
  'FAILED_DELIVERY_FEE',
];
const KNOWN_WAREHOUSE_RATE_TYPES: string[] = [
  'STORAGE_PER_BOX_MONTH',
  'INBOUND_PROCESSING',
  'OUTBOUND_PICK',
  'WH_FAILED_DELIVERY_FEE',
];

const RATE_TYPE_LABELS: Record<string, string> = {
  DOORSTEP_COLLECTION: 'Doorstep collection',
  TIMESLOT_COLLECTION: 'Timeslot collection',
  DOORSTEP_RETURN: 'Doorstep return',
  TIMESLOT_RETURN: 'Timeslot return',
  MIN_CHARGE: 'Minimum charge',
  FAILED_DELIVERY_FEE: 'Failed delivery fee',
  STORAGE_PER_BOX_MONTH: 'Storage per box / month',
  INBOUND_PROCESSING: 'Inbound processing',
  OUTBOUND_PICK: 'Outbound pick',
  WH_FAILED_DELIVERY_FEE: 'Warehouse failed delivery fee',
};

function displayRateType(key: string): string {
  if (RATE_TYPE_LABELS[key]) return RATE_TYPE_LABELS[key];
  // Fallback for legacy / unknown keys returned by the API.
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

type PricingScope = 'all' | 'courier' | 'warehouse';

interface Props {
  partnerId: number;
  /**
   * When true, hide the AddButton + per-row edit/delete affordances and
   * disable the Save button. The inline number/text/select inputs stay
   * mounted so values render, but the save round-trip is gated.
   * Default: false (admin behaviour).
   */
  readOnly?: boolean;
  /**
   * Limits which sub-scope tabs render. `'courier'` shows only the courier
   * sub-tab; `'warehouse'` shows only the warehouse sub-tab; `'all'`
   * shows both (today's behaviour).
   * Default: `'all'`.
   */
  pricingScope?: PricingScope;
}

interface RateRow extends BillingRateDTO {
  __index: number;
}

interface FeeRow extends PartnerAdditionalFeeDTO {
  __index: number;
}

export default function PricingTab({
  partnerId,
  readOnly = false,
  pricingScope = 'all',
}: Props) {
  const initialScope: Scope = pricingScope === 'warehouse' ? 'warehouse' : 'courier';
  const [scope, setScope] = useState<Scope>(initialScope);
  const showCourierTab = pricingScope === 'all' || pricingScope === 'courier';
  const showWarehouseTab = pricingScope === 'all' || pricingScope === 'warehouse';
  const [rates, setRates] = useState<BillingRateDTO[]>([]);
  const [additionalFees, setAdditionalFees] = useState<PartnerAdditionalFeeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [netError, setNetError] = useState<string>();
  const [saveBanner, setSaveBanner] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);
    setSaveBanner(undefined);

    const url = `/admin/partners/${partnerId}/${scope}-pricing`;
    axiosKK
      .get<PartnerPricingResponse>(url)
      .then(({ data }) => {
        if (cancelled) return;
        setRates(data.rates || []);
        setAdditionalFees(data.additionalFees || []);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [partnerId, scope]);

  const knownKeys = scope === 'courier' ? KNOWN_COURIER_RATE_TYPES : KNOWN_WAREHOUSE_RATE_TYPES;
  const presentKeys = new Set(rates.map((r) => r.rateType));
  const missingKnown = knownKeys.filter((k) => !presentKeys.has(k));

  const updateRate = (index: number, patch: Partial<BillingRateDTO>) => {
    setRates((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRateRow = (rateType: string) => {
    const suggested = DEFAULT_RATE_AMOUNTS_PENCE[rateType] ?? 0;
    setRates((prev) => [...prev, { rateType, rateAmount: suggested, warehouseId: null }]);
  };

  const removeRateRow = (index: number) => {
    setRates((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFee = (index: number, patch: Partial<PartnerAdditionalFeeDTO>) => {
    setAdditionalFees((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const addFee = () => {
    setAdditionalFees((prev) => [
      ...prev,
      { label: '', amountPence: 0, applyMode: 'FLAT' as ApplyMode },
    ]);
  };

  const removeFee = (index: number) => {
    setAdditionalFees((prev) => prev.filter((_, i) => i !== index));
  };

  const onSave = () => {
    setSaving(true);
    setNetError(undefined);
    setSaveBanner(undefined);

    const cleanFees = additionalFees.filter((f) => f.label.trim() !== '');
    const validKeys = new Set(knownKeys);
    const cleanRates = rates.filter((r) => validKeys.has(r.rateType));

    const payload: PartnerPricingRequest = {
      rates: cleanRates.map((r) => ({
        rateType: r.rateType,
        rateAmount: Number(r.rateAmount) || 0,
        warehouseId: null,
      })),
      additionalFees: cleanFees.map((f) => ({
        label: f.label.trim(),
        amountPence: Number(f.amountPence) || 0,
        applyMode: f.applyMode,
      })),
    };

    axiosKK
      .put<PartnerPricingResponse>(`/admin/partners/${partnerId}/${scope}-pricing`, payload)
      .then(({ data }) => {
        setRates(data.rates || []);
        setAdditionalFees(data.additionalFees || []);
        setSaveBanner('Pricing saved.');
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSaving(false));
  };

  const rateRows: RateRow[] = rates.map((r, i) => ({ ...r, __index: i }));
  const rateColumns: DataTableColumn<RateRow>[] = [
    { key: 'rateType', label: 'Rate type', render: (r) => displayRateType(r.rateType) },
    {
      key: 'rateAmount',
      label: 'Amount (pence)',
      align: 'numeric',
      render: (r) => {
        const inputId = `rate-amount-${scope}-${r.__index}`;
        return readOnly ? (
          <span>{r.rateAmount}</span>
        ) : (
          <>
            <label htmlFor={inputId} className="visually-hidden">
              Amount in pence for {displayRateType(r.rateType)}
            </label>
            <input
              id={inputId}
              name={inputId}
              type="number"
              min={0}
              value={r.rateAmount}
              onChange={(e) =>
                updateRate(r.__index, { rateAmount: Number(e.target.value) })
              }
            />
          </>
        );
      },
    },
  ];

  const feeRows: FeeRow[] = additionalFees.map((f, i) => ({ ...f, __index: i }));
  const feeColumns: DataTableColumn<FeeRow>[] = [
    {
      key: 'label',
      label: 'Label',
      render: (f) => {
        const inputId = `fee-label-${scope}-${f.__index}`;
        return readOnly ? (
          <span>{f.label}</span>
        ) : (
          <>
            <label htmlFor={inputId} className="visually-hidden">
              Fee label
            </label>
            <input
              id={inputId}
              name={inputId}
              type="text"
              value={f.label}
              onChange={(e) =>
                updateFee(f.__index, { label: e.target.value })
              }
            />
          </>
        );
      },
    },
    {
      key: 'amountPence',
      label: 'Amount (pence)',
      align: 'numeric',
      render: (f) => {
        const inputId = `fee-amount-${scope}-${f.__index}`;
        return readOnly ? (
          <span>{f.amountPence}</span>
        ) : (
          <>
            <label htmlFor={inputId} className="visually-hidden">
              Fee amount in pence
            </label>
            <input
              id={inputId}
              name={inputId}
              type="number"
              min={0}
              value={f.amountPence}
              onChange={(e) =>
                updateFee(f.__index, { amountPence: Number(e.target.value) })
              }
            />
          </>
        );
      },
    },
    {
      key: 'applyMode',
      label: 'Apply mode',
      render: (f) => {
        const inputId = `fee-mode-${scope}-${f.__index}`;
        return readOnly ? (
          <span>{f.applyMode}</span>
        ) : (
          <>
            <label htmlFor={inputId} className="visually-hidden">
              Apply mode
            </label>
            <select
              id={inputId}
              name={inputId}
              value={f.applyMode}
              onChange={(e) =>
                updateFee(f.__index, { applyMode: e.target.value as ApplyMode })
              }
            >
              {APPLY_MODES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </>
        );
      },
    },
  ];

  return (
    <section className="admin-partner-pricing">
      {pricingScope === 'all' && (
        <div className="admin-tab-bar admin-tab-bar--sub">
          {showCourierTab && (
            <button
              type="button"
              className={scope === 'courier' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
              onClick={() => setScope('courier')}
            >
              Courier
            </button>
          )}
          {showWarehouseTab && (
            <button
              type="button"
              className={scope === 'warehouse' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
              onClick={() => setScope('warehouse')}
            >
              Warehouse
            </button>
          )}
        </div>
      )}

      <AdminNotice intent="error">{netError}</AdminNotice>
      <AdminNotice intent="success">{saveBanner}</AdminNotice>

      {loading ? (
        <p>Loading pricing…</p>
      ) : (
        <>
          <div className="pricing-section">
            <h4>Rates</h4>
            <p className="pricing-hint">
              Amounts are in pence. Set a rate to 0 to disable it — the invoice will drop the line.
              New rates added via the buttons below are pre-filled with UK 2026 default amounts that you can edit.
            </p>
            <DataTable<RateRow>
              columns={rateColumns}
              rows={rateRows}
              keyOf={(r) => `${r.rateType}-${r.__index}`}
              emptyMessage="No rates configured."
              actionsColumn={
                readOnly
                  ? undefined
                  : {
                      render: (r) => (
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => removeRateRow(r.__index)}
                        >
                          Remove
                        </button>
                      ),
                    }
              }
            />

            {!readOnly && missingKnown.length > 0 && (
              <div className="pricing-add-known">
                <Label>Add rate type:</Label>
                <div className="pricing-add-known__buttons">
                  {missingKnown.map((k) => (
                    <button
                      key={k}
                      type="button"
                      className="pricing-add-known__button"
                      onClick={() => addRateRow(k)}
                    >
                      + {displayRateType(k)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="pricing-section">
            <h4>Additional fees</h4>
            <p className="pricing-hint">
              Leave the list empty to clear all additional fees on save.
            </p>
            <DataTable<FeeRow>
              columns={feeColumns}
              rows={feeRows}
              keyOf={(f) => `fee-${f.__index}`}
              emptyMessage="No additional fees configured."
              aboveTable={
                readOnly ? undefined : <AddButton onClick={addFee}>Add fee</AddButton>
              }
              actionsColumn={
                readOnly
                  ? undefined
                  : {
                      render: (f) => (
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => removeFee(f.__index)}
                        >
                          Remove
                        </button>
                      ),
                    }
              }
            />
          </div>

          {!readOnly && (
            <SubmitButton onClick={onSave} isSubmitting={saving} disabled={saving}>
              Save pricing
            </SubmitButton>
          )}
        </>
      )}
    </section>
  );
}
